import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pollImportJob } from "@/lib/importJob";
import { FileUp, Loader2, Wand2, ShieldCheck, ShieldAlert, FileSearch } from "lucide-react";

// Import a LONE modified file: the backend auto-recognises the matching original (ORI)
// already in the library, computes the differential and registers the solution.
export const ModImport = ({ services, onImported }) => {
  const [file, setFile] = useState(null);
  const [svc, setSvc] = useState([]);
  const [brand, setBrand] = useState("");
  const [ecu, setEcu] = useState("");
  const [tunedWith, setTunedWith] = useState("");
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const toggle = (id) => setSvc((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    if (!file) return toast.error("Ajoutez le fichier modifié");
    if (svc.length === 0) return toast.error("Sélectionnez au moins un service (ex. Stage 1)");
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("services", svc.join(","));
    if (brand) fd.append("brand", brand);
    if (ecu) fd.append("ecu", ecu);
    if (tunedWith) fd.append("tuned_with", tunedWith);
    if (hp) fd.append("power_hp", hp);
    try {
      const { data: started } = await api.post("/admin/solutions/import_mod", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const job = await pollImportJob(started.job_id);
      if (job.status === "failed") {
        toast.error(job.fail_summary || job.error || "Erreur d'import");
      } else {
        const data = job.result || {};
        setResult(data);
        if (data.matched) {
          toast.success(`Concordance trouvée · ${data.ref} créé (${data.changed_bytes} octets)`);
          setFile(null); if (fileRef.current) fileRef.current.value = "";
          onImported?.();
        } else {
          toast.warning(data.detail || "Aucun fichier original concordant en base");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur d'import");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600";

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="mod-import">
      <div className="flex items-center gap-2 mb-1">
        <FileSearch className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Importer un MOD seul · reconnaissance auto</h3>
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-5 leading-snug">
        Déposez <b className="text-white">un seul fichier modifié</b> (ex. Stage 1). Le moteur cherche automatiquement le <b className="text-aurora-cyan">fichier original (ORI) concordant</b> déjà présent en base, calcule le différentiel et enregistre la solution — sans réimporter l'ORI.
      </p>

      <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Service(s) de ce fichier</label>
      <div className="flex flex-wrap gap-2 mb-4">
        {services.map((s) => {
          const on = svc.includes(s.id);
          return (
            <button key={s.id} data-testid={`modimport-svc-${s.id}`} onClick={() => toggle(s.id)} className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest rounded-sm border transition-colors ${on ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              {s.name}
            </button>
          );
        })}
      </div>

      <div className="mb-4">
        <input ref={fileRef} type="file" className="hidden" data-testid="modimport-file-input" onChange={(e) => setFile(e.target.files[0])} />
        <button onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-aurora-cyan/40 hover:border-aurora-cyan transition-colors">
          <FileUp className="w-5 h-5 text-aurora-cyan" />
          <span className="font-mono text-xs text-zinc-300 truncate">{file ? file.name : "Fichier MODIFIÉ (ex. stage1.bin)"}</span>
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <input className={inputCls} data-testid="modimport-brand" placeholder="Marque (opt.)" value={brand} onChange={(e) => setBrand(e.target.value)} />
        <input className={inputCls} data-testid="modimport-ecu" placeholder="ECU (opt.)" value={ecu} onChange={(e) => setEcu(e.target.value)} />
        <input className={inputCls} placeholder="Outil (opt.)" value={tunedWith} onChange={(e) => setTunedWith(e.target.value)} />
        <input className={inputCls} type="number" placeholder="CV (opt.)" value={hp} onChange={(e) => setHp(e.target.value)} />
      </div>

      <button data-testid="modimport-submit-btn" onClick={submit} disabled={busy} className="flex items-center gap-2 px-6 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Reconnaître & assimiler
      </button>

      {result && result.matched && (
        <div className="mt-5 rounded-sm border border-signal-success/30 bg-signal-success/5 p-4" data-testid="modimport-result-ok">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-signal-success" />
            <span className="font-display text-sm font-bold uppercase text-signal-success">Concordance trouvée · {result.ref}</span>
          </div>
          <div className="font-mono text-[11px] text-zinc-300 space-y-1">
            <div>ORI concordant : {result.original_hash?.slice(0, 16)}… · {result.brand || "—"} / {result.ecu || "—"}</div>
            <div>{result.changed_bytes} octets modifiés ({(result.ratio * 100).toFixed(2)}% du fichier) · services : {(result.services || []).join(", ")}</div>
          </div>
        </div>
      )}
      {result && !result.matched && (
        <div className="mt-5 rounded-sm border border-signal-processing/30 bg-signal-processing/5 p-4" data-testid="modimport-result-none">
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="w-4 h-4 text-signal-processing" />
            <span className="font-display text-sm font-bold uppercase text-signal-processing">Aucun ORI concordant</span>
          </div>
          <p className="font-mono text-[10px] text-zinc-400">{result.detail}</p>
        </div>
      )}
    </div>
  );
};
